import { useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";
import { cn } from "@/cn";
import { useProfile, useUpdateProfile } from "@/hooks/useApi";
import {
  applyTheme,
  cacheThemePreference,
  isThemePreference,
  resolveTheme,
  subscribeToSystemTheme,
  type ThemePreference,
} from "@/lib/theme";

export function ThemeToggle({ className }: { className?: string }) {
  const { data: profile } = useProfile();
  const updateProfile = useUpdateProfile();
  const preference: ThemePreference = isThemePreference(profile?.themePreference)
    ? profile.themePreference
    : "system";
  const [dark, setDark] = useState(() => resolveTheme(preference) === "dark");

  useEffect(() => {
    const sync = () => setDark(document.documentElement.classList.contains("dark"));
    sync();
    window.addEventListener("themechange", sync);
    const unsubscribe = subscribeToSystemTheme(sync);
    return () => {
      window.removeEventListener("themechange", sync);
      unsubscribe();
    };
  }, []);

  function toggle() {
    const next: ThemePreference = dark ? "light" : "dark";
    cacheThemePreference(next);
    applyTheme(next);
    updateProfile.mutate(
      { themePreference: next },
      {
        onError: () => {
          cacheThemePreference(preference);
          applyTheme(preference);
        },
      },
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={updateProfile.isPending}
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      title={dark ? "Light mode" : "Dark mode"}
      className={cn(
        "inline-flex items-center justify-center rounded-md p-1.5 text-muted transition-colors hover:bg-surface-hover hover:text-foreground max-sm:min-h-[44px] max-sm:min-w-[44px]",
        className,
      )}
    >
      {dark ? <Sun className="size-4" /> : <Moon className="size-4" />}
    </button>
  );
}